import { Location } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import {
  FORTRESS_CONFIG,
  FortressConfig,
  PermissionCheckService,
  UserPermissionCheckService,
} from '@psu-pennstateit-swe/apis/fortress';
import { SEARCH_CONFIG, SearchConfig } from '@psu-pennstateit-swe/apis/search';
import { LoadingEvents, RequestCacheInterceptor } from '@psu-pennstateit-swe/utils/browser';
import { CDN_CONFIG, CdnConfig } from '@psu-pennstateit-swe/utils/cdn';
import {
  AUTH_CONFIG_DD,
  AuthConfigWithDiscoveryDoc,
  AuthInterceptor,
  AuthModule,
  AuthService,
  OAuthErrorEvent,
} from '@psu-pennstateit-swe/utils/security';
import { provideNgxSkeletonLoader } from 'ngx-skeleton-loader';
import { filter, takeWhile } from 'rxjs';
import { properties } from '../environments/environment';
import { APP_PROPERTIES, FortressProperties } from '../environments/fortress.properties.model';
import { routes } from './app.routes';

const cdnFactory = (props: FortressProperties): CdnConfig => props.cdn;
const fortressFactory = (props: FortressProperties): FortressConfig => props.fortress;
const oauthFactory = (props: FortressProperties): AuthConfigWithDiscoveryDoc => props.oauth;
const searchFactory = (props: FortressProperties): SearchConfig => props.searchService;
const initializeAppFactory =
  (
    authConfig: AuthConfigWithDiscoveryDoc,
    authService: AuthService,
    loadingEvents: LoadingEvents,
    location: Location,
  ): (() => Promise<void>) =>
  async () => {
    authService.configure(authConfig);

    if (authConfig.showDebugInformation) {
      authService.enableDebug();
    }

    // Listen for error events until a token_received event is emitted
    authService.events
      .pipe(
        takeWhile((event) => event.type !== 'token_received'),
        filter((event): event is OAuthErrorEvent => 'reason' in event),
      )
      .subscribe((event) => {
        document.dispatchEvent(
          new CustomEvent('apperror', {
            bubbles: true,
            detail: event,
          }),
        );
      });

    await authService.loadDiscoveryDocument(authConfig.discoveryDocumentUrl);
    await authService.tryLogin();

    if (authService.isLoggedIn()) {
      authService.setupAutomaticSilentRefresh();
      loadingEvents.triggerAppReady();
    } else {
      authService.initLoginFlow(location.path());
      return new Promise(() => {
        /* This promise will never be settled which prevents the app from loading */
      });
    }
  };

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(AuthModule),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(() => {
      const initializerFn = initializeAppFactory(
        inject(AUTH_CONFIG_DD),
        inject(AuthService),
        inject(LoadingEvents),
        inject(Location),
      );
      return initializerFn();
    }),
    provideNgxSkeletonLoader({
      theme: {
        extendsFromRoot: true,
        height: '30px',
      },
    }),
    { provide: APP_PROPERTIES, useValue: properties },
    { provide: AUTH_CONFIG_DD, useFactory: oauthFactory, deps: [APP_PROPERTIES] },
    { provide: CDN_CONFIG, useFactory: cdnFactory, deps: [APP_PROPERTIES] },
    { provide: FORTRESS_CONFIG, useFactory: fortressFactory, deps: [APP_PROPERTIES] },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RequestCacheInterceptor, multi: true },
    { provide: PermissionCheckService, useClass: UserPermissionCheckService },
    { provide: SEARCH_CONFIG, useFactory: searchFactory, deps: [APP_PROPERTIES] },
  ],
};
